import { randomId } from '../utils/randomId';
import { normalizeNextGameParticipation } from '../domain/nextGameParticipation';
import { GameEvent, GameState, Player } from '../types/game';
import { OnlineSnapshot } from '../types/snapshots';
import { OnlineLobbyStateSnapshot } from '../types/transport';
import { sanitizePlayerName } from '../utils/sanitize.ts';
import { assignPlayerColors, pickNextPlayerColor } from '../utils/playerColors';
import { validateAndComputeScore, applyScoreToPlayer } from '../domain/scoreComputation';
import { classifyGameRestartedPayload } from '../domain/gameRestarted';
import { applyTurnStats } from '../domain/stats';
import { computeTurnAdvance, pushNotice, updatePlayerPositions, withDefaultStatus } from './reducerHelpers';
import { isVerboseLoggingEnabled, logger } from '../utils/logger';
import { perf } from '../utils/perfMetrics';
import { handleDiceRollEvent, handleDiceKeepEvent, handleTurnEnd } from './handlers/turn/turnIndex';
import { gameRules, makeEmptyDiceMask } from '../domain/gameRules';
import { revealZone } from '../domain/zones';

function hasScoreSettlementForTurn(state: GameState, turn: number, playerId: string): boolean {
  return (state.events || []).some(
    (ev) => ev.type === 'SCORE' && ev.turn === turn && ev.payload?.playerId === playerId,
  );
}

function hasPenaltySettlementForTurn(
  state: GameState,
  turn: number,
  playerId: string,
  reason?: string,
): boolean {
  return (state.events || []).some(
    (ev) => ev.type === 'PENALTY'
      && ev.turn === turn
      && ev.payload?.playerId === playerId
      && (reason === undefined || ev.payload?.reason === reason),
  );
}

export function handleHydrateFromSnapshot(state: GameState, snap: OnlineSnapshot): GameState {
  if (snap.gameId && state.id && snap.gameId !== state.id) {
    if (isVerboseLoggingEnabled()) {
      logger.warn('HYDRATE_SNAPSHOT_GAME_MISMATCH', {
        currentGameId: state.id,
        snapshotGameId: snap.gameId,
      });
    }
    return state;
  }
  const fe = snap.firstEntries || {};
  const lobbyOrder: string[] = (snap.lobby && snap.lobby.order) || state.lobby?.order || [];
  const orderIndex = new Map<string, number>();
  lobbyOrder.forEach((id: string, idx: number) => orderIndex.set(id, idx));
  state.players.forEach((p, idx) => {
    if (!orderIndex.has(p.id)) {
      orderIndex.set(p.id, idx);
    }
  });
  const playersWithScores = state.players.map((p) => ({
    ...p,
    score: snap.scores?.[p.id] ?? p.score,
    hasFirstEntry: fe[p.id] ? true : p.hasFirstEntry,
    status: snap.playerStatuses?.[p.id] || p.status || 'active',
    role: p.role || 'player',
  }));
  const sorted = [...playersWithScores].sort((a, b) => {
    const scoreDiff = b.score - a.score;
    if (scoreDiff !== 0) return scoreDiff;
    const aIdx = orderIndex.get(a.id) ?? 0;
    const bIdx = orderIndex.get(b.id) ?? 0;
    return aIdx - bIdx;
  });
  const players = playersWithScores.map((p) => ({
    ...p,
    position: sorted.findIndex(sp => sp.id === p.id) + 1,
  }));
  const coloredPlayers = assignPlayerColors(players);
  const winScore = gameRules.scoring.winScore;
  const existingWinnerId = state.winner?.id || null;
  let computedWinnerId: string | null = null;
  if (!existingWinnerId) {
    const eligible = coloredPlayers
      .map((p) => ({
        id: p.id,
        score: p.score ?? 0,
        order: orderIndex.get(p.id) ?? p.position ?? 0,
      }))
      .filter((p) => p.score >= winScore)
      .sort((a, b) => {
        const scoreDiff = b.score - a.score;
        if (scoreDiff !== 0) return scoreDiff;
        return a.order - b.order;
      });
    computedWinnerId = eligible.length > 0 ? eligible[0].id : null;
  }
  const winnerId = existingWinnerId || computedWinnerId;
  const playersWithWinner = winnerId
    ? coloredPlayers.map((p) => ({ ...p, isWinner: p.id === winnerId }))
    : coloredPlayers;
  const winner = winnerId
    ? (playersWithWinner.find((p) => p.id === winnerId) || state.winner)
    : state.winner;
  const activeIdx = playersWithWinner.findIndex(p => p.id === snap.activePlayerId);
  const rawLobbyPhase = (snap.lobby && snap.lobby.phase) || state.lobby?.phase || 'LOBBY';
  const snapshotSignalsInGame = Boolean(snap.activePlayerId) || (snap.rollIndex || 0) > 0;
  const shouldPreserveInGame =
    rawLobbyPhase === 'LOBBY'
    && (
      snapshotSignalsInGame
      || (
        state.lobby?.phase === 'IN_GAME'
        && Boolean(state.turnCycle?.activePlayerId)
        && (snap.turn || 0) >= (state.turnNumber || 0)
      )
    );
  const lobbyPhase = shouldPreserveInGame ? 'IN_GAME' : rawLobbyPhase;
  const lobbyReady = (snap.lobby && snap.lobby.readyMap) || state.lobby?.readyMap || {};
  const nextGameParticipation = normalizeNextGameParticipation(
    (snap.lobby && snap.lobby.nextGameParticipation) || state.lobby?.nextGameParticipation,
  );
  const allowTurnCycle = lobbyPhase === 'IN_GAME' || lobbyPhase === 'ORDER_REVEAL';
  const isNewTurn = snap.turn && snap.turn !== state.turnNumber;
  const shouldResetTurn = allowTurnCycle && (isNewTurn || (snap.activePlayerId && state.turnCycle?.activePlayerId && snap.activePlayerId !== state.turnCycle.activePlayerId));
  const missingTurnDice = allowTurnCycle && !shouldResetTurn && snap.rollIndex > 0 && (!state.turnCycle?.dice || state.turnCycle.dice.length === 0);
  const snapshotTurnCycle = snap.turnCycle || null;
  const settingsFromSnap = snap.settings || state.settings || { tunnels: true, rescueByFive: true };
  const turnCycle = allowTurnCycle
    ? (snapshotTurnCycle
      ? {
          ...snapshotTurnCycle,
          activePlayerId: snapshotTurnCycle.activePlayerId || snap.activePlayerId || undefined,
          rollCount: snapshotTurnCycle.rollCount ?? snap.rollIndex ?? 0,
          dice: snapshotTurnCycle.dice || [],
          kept: snapshotTurnCycle.kept || makeEmptyDiceMask(),
          capturedMask: snapshotTurnCycle.capturedMask || makeEmptyDiceMask(),
          status: snapshotTurnCycle.status || 'IDLE',
          diceOrigins: snapshotTurnCycle.diceOrigins || [],
        }
      : (snap.activePlayerId
        ? {
            activePlayerId: snap.activePlayerId,
            rollCount: snap.rollIndex || 0,
            dice: shouldResetTurn || missingTurnDice ? [] : (state.turnCycle?.dice || []),
            kept: shouldResetTurn || missingTurnDice ? makeEmptyDiceMask() : (state.turnCycle?.kept || makeEmptyDiceMask()),
            capturedMask: shouldResetTurn || missingTurnDice ? makeEmptyDiceMask() : (state.turnCycle?.capturedMask || makeEmptyDiceMask()),
            status: shouldResetTurn || missingTurnDice ? 'IDLE' : (state.turnCycle?.status || 'IDLE'),
            lastRoll: shouldResetTurn || missingTurnDice ? undefined : state.turnCycle?.lastRoll,
            diceOrigins: shouldResetTurn || missingTurnDice ? [] : state.turnCycle?.diceOrigins,
          }
        : state.turnCycle))
    : undefined;
  const snapSpectators = Array.isArray(snap.spectators) ? snap.spectators : null;
  const nextSpectators = snapSpectators && snapSpectators.length > 0
    ? snapSpectators
    : (state.spectators || []);
  return {
    ...state,
    id: snap.gameId || state.id,
    roomId: snap.roomId || state.roomId || state.groupToken || null,
    currentRoundId: snap.currentRoundId || state.currentRoundId || snap.gameId || state.id,
    mode: snap.mode || state.mode,
    turnNumber: snap.turn || state.turnNumber,
    currentPlayerIndex: activeIdx >= 0 ? activeIdx : state.currentPlayerIndex,
    firstEntries: fe,
    players: playersWithWinner,
    spectators: nextSpectators,
    settings: settingsFromSnap,
    // Snapshot niesie wyłącznie strefy odkryte; nieodkryte zostają po stronie serwera.
    zones: snap.zones ?? state.zones,
    rapidTurnMeta: snap.rapidTurnMeta ?? null,
    rapidPenaltyPresentation: snap.rapidPenaltyPresentation ?? null,
    postGameSummary: snap.postGameSummary ?? null,
    isGameOver: state.isGameOver || Boolean(winnerId),
    winner,
    lobby: {
      phase: lobbyPhase,
      order: lobbyOrder,
      readyMap: lobbyReady,
      nextGameParticipation: { ...nextGameParticipation },
    },
    turnCycle,
    segments: shouldResetTurn ? [] : state.segments,
    segmentsOwnerId: shouldResetTurn ? undefined : state.segmentsOwnerId,
  };
}

export function handleHydrateLobbyState(state: GameState, snapshot: OnlineLobbyStateSnapshot): GameState {
  if (state.mode === 'REAL') return state;
  if (!snapshot.gameId || (state.id && state.id !== snapshot.gameId)) return state;
  const localActivePlayers = state.players.filter(
    (player) => (player.role || 'player') === 'player' && (player.status || 'active') === 'active',
  );
  // PartyKit can briefly send an empty LOBBY_STATE right after restart (before seeded ADD_PLAYER events arrive).
  // In that window we prefer local state to avoid flashing the "host name" form.
  if (snapshot.players.length === 0 && localActivePlayers.length > 0) {
    if (isVerboseLoggingEnabled()) {
      logger.debug('HYDRATE_LOBBY_STATE_SKIP_EMPTY_SNAPSHOT', {
        gameId: snapshot.gameId,
        localActivePlayers: localActivePlayers.length,
        localPlayers: state.players.length,
        localPhase: state.lobby?.phase || null,
        snapshotPhase: snapshot.phase,
      });
    }
    return state;
  }

  const existingById = new Map(state.players.map((player) => [player.id, player] as const));
  const incomingPlayers: Player[] = snapshot.players.map((incoming, index) => {
    const existing = existingById.get(incoming.id);
    const base: Player = existing
      ? { ...existing }
      : {
          id: incoming.id,
          name: incoming.name,
          score: 0,
          position: index + 1,
          hasFirstEntry: false,
          isWinner: false,
          isHost: false,
          statistics: {
            totalTurns: 0,
            totalPasses: 0,
            totalNegativePoints: 0,
            averageScore: 0,
            totalOvertakes: 0,
            totalPointsDelta: 0,
          },
        };

    return withDefaultStatus({
      ...base,
      name: incoming.name,
      authUserId: incoming.authUserId ?? existing?.authUserId ?? null,
      role: incoming.role,
      status: incoming.status,
      isHost: incoming.isHost,
      isBot: incoming.isBot === true,
      botProfile: incoming.botProfile,
      position: index + 1,
    } as Player);
  });

  const normalizedPlayers = assignPlayerColors(incomingPlayers);
  const normalizedOrder = (snapshot.order || [])
    .filter((id) => normalizedPlayers.some((player) => player.id === id));
  const activePlayerId = snapshot.activePlayerId
    || (normalizedOrder.length > 0 ? normalizedOrder[0] : null);
  const currentPlayerIndex = activePlayerId
    ? normalizedPlayers.findIndex((player) => player.id === activePlayerId)
    : state.currentPlayerIndex;
  // LOBBY_STATE jest tylko meta-snapshotem lobby i bywa opóźniony względem SNAPSHOT/EVENT_APPLIED.
  // W trakcie IN_GAME nie nadpisujemy istniejącego turnCycle, bo może to cofnąć activePlayerId/kości.
  const nextTurnCycle: GameState['turnCycle'] = snapshot.phase === 'IN_GAME'
    ? (state.turnCycle
      ? state.turnCycle
      : {
          activePlayerId: activePlayerId || undefined,
          rollCount: 0,
          dice: [],
          kept: makeEmptyDiceMask(),
          capturedMask: makeEmptyDiceMask(),
          status: 'IDLE' as const,
          lastRoll: undefined,
          diceOrigins: [],
        })
    : undefined;
  const nextCurrentPlayerIndex = (
    snapshot.phase === 'IN_GAME' && state.turnCycle
      ? state.currentPlayerIndex
      : (currentPlayerIndex >= 0 ? currentPlayerIndex : state.currentPlayerIndex)
  );
  const clampedCurrentPlayerIndex = normalizedPlayers.length > 0
    ? Math.min(Math.max(nextCurrentPlayerIndex, 0), normalizedPlayers.length - 1)
    : 0;

  return {
    ...state,
    id: snapshot.gameId,
    roomId: snapshot.roomId || state.roomId || state.groupToken || null,
    currentRoundId: snapshot.currentRoundId || state.currentRoundId || snapshot.gameId,
    players: normalizedPlayers,
    lobby: {
      phase: snapshot.phase,
      readyMap: { ...snapshot.readyMap },
      nextGameParticipation: normalizeNextGameParticipation(snapshot.nextGameParticipation),
      ...(normalizedOrder.length > 0 ? { order: normalizedOrder } : {}),
    },
    currentPlayerIndex: clampedCurrentPlayerIndex,
    turnCycle: nextTurnCycle,
  };
}

// Ślad `APPLY_EVENT` powstaje przy dispatchu (`dispatchApplyEvent`), nie tutaj – reducer
// musi zostać czysty, żeby StrictMode i replay po ROLLBACK_EVENT nie dublowały wpisów.
export function handleApplyEvent({ state, event }: { state: GameState; event: GameEvent }): GameState {
  const appendEventOnly = () => ({ ...state, events: [...(state.events || []), event] });
  // --- Global event duplicate guard (by id) ---
  if ((state.events || []).some(e => e.id === event.id)) { perf.inc('guard.event.duplicate'); return state; }
  let newState: GameState;
  switch (event.type) {
    case 'GAME_CREATED': {
      const mode = event.payload?.mode === 'REAL' ? 'REAL' : 'ONLINE';
      newState = {
        ...state,
        mode,
        events: [...(state.events || []), event],
      };
      break;
    }
    case 'ADD_PLAYER': {
      const { playerId, name, color: payloadColor, role: payloadRole, isBot: payloadIsBot, botProfile } = event.payload;
      if (state.players.some(p => p.id === playerId)) { perf.inc('guard.event.playerDuplicate'); return { ...state, events: [...(state.events || []), event] }; } // dodatkowa ochrona przed duplikatami
      const sanitized = sanitizePlayerName(String(name||''));
      if (!sanitized) { return { ...state, events: [...(state.events || []), event] }; }
      const role: 'player' | 'viewer' = payloadRole === 'viewer' ? 'viewer' : 'player';
      const gameStarted = state.mode === 'ONLINE' && state.lobby?.phase === 'IN_GAME';
      const shouldForceViewer = role === 'viewer' || gameStarted;
      const activeId = state.turnCycle?.activePlayerId || state.players[state.currentPlayerIndex]?.id || null;
      if (shouldForceViewer) {
        const existingSpectators = [...(state.spectators || [])];
        if (existingSpectators.some(s => s.id === playerId)) {
          newState = { ...state, events: [...(state.events || []), event] };
          break;
        }
        const nextSpectators = [...existingSpectators, { id: playerId, name: sanitized, joinedAt: event.timestamp }];
        newState = { ...state, spectators: nextSpectators, events: [...(state.events || []), event] };
        break;
      }
      if (state.players.some(p => p.name.toLowerCase() === sanitized.toLowerCase())) { return { ...state, events: [...(state.events || []), event] }; }
      const usedColors = new Set(state.players.map((p) => p.color).filter(Boolean) as string[]);
      const validColor = typeof payloadColor === 'string' && payloadColor.length > 0 && !usedColors.has(payloadColor);
      const chosenColor = validColor ? payloadColor : pickNextPlayerColor(usedColors);
      const newPlayer: Player = {
        id: playerId,
        name: sanitized,
        score: 0,
        position: state.players.length + 1,
        color: chosenColor,
        hasFirstEntry: false,
        isWinner: false,
        statistics: { totalTurns: 0, totalPasses: 0, totalNegativePoints: 0, averageScore: 0, totalOvertakes: 0, totalPointsDelta: 0 },
        status: 'active',
        role: 'player',
        isBot: payloadIsBot === true,
        botProfile,
      };
      const playersAfter = [...state.players, newPlayer];
      const nextOrder = (state.lobby?.order && state.lobby.order.length > 0)
        ? [...state.lobby.order, playerId]
        : state.lobby?.order;
      const nextReadyMap = state.lobby?.readyMap ? { ...state.lobby.readyMap, [playerId]: true } : { [playerId]: true };
      const nextCurrentIndex = activeId ? playersAfter.findIndex(p => p.id === activeId) : state.currentPlayerIndex;
      newState = {
        ...state,
        players: playersAfter,
        currentPlayerIndex: nextCurrentIndex >= 0 ? nextCurrentIndex : state.currentPlayerIndex,
        events: [...(state.events || []), event],
        lobby: state.mode === 'ONLINE' && state.lobby ? {
          ...state.lobby,
          readyMap: nextReadyMap,
          ...(nextOrder ? { order: nextOrder } : {}),
        } : state.lobby,
      };
      break;
    }
    case 'SCORE': {
      const payload = event.payload;
      const playerId = payload.playerId;
      const deltaRaw = Number(payload.delta ?? 0);
      const delta = Number.isFinite(deltaRaw) ? deltaRaw : 0;
      const playerIndex = state.players.findIndex(p => p.id === playerId);
      if (playerIndex === -1) { newState = { ...state, events: [...(state.events || []), event] }; break; }

      const hasScoreForTurn = hasScoreSettlementForTurn(state, event.turn, playerId);
      const hasBlockingPenaltyForTurn = hasPenaltySettlementForTurn(state, event.turn, playerId);
      if (hasScoreForTurn || hasBlockingPenaltyForTurn) {
        newState = { ...state, events: [...(state.events || []), event] };
        break;
      }
      const prevPlayer = state.players[playerIndex];
      const prevScore = prevPlayer.score;
      const rawResulting = payload.resultingScore;
      const parsedResulting = rawResulting == null ? undefined : Number(rawResulting);
      const resolvedResulting = Number.isFinite(parsedResulting) ? (parsedResulting as number) : prevScore + delta;
      const normalizedEvent = {
        ...event,
        payload: { ...payload, resultingScore: resolvedResulting },
      };
      const nextEvents = [...(state.events || []), normalizedEvent];
      const canUseSingleDie = prevPlayer.score >= gameRules.scoring.minScoringDice.thresholdScore;
      const validation = validateAndComputeScore(
        delta,
        prevPlayer.score,
        prevPlayer.hasFirstEntry,
        canUseSingleDie,
        state.settings.tunnels,
      );
      if (!validation.isValid) {
        if (isVerboseLoggingEnabled()) {
          logger.warn('SCORE_TUNNEL_GUARD_BLOCK', { playerId, delta, prevScore: prevPlayer.score });
        }
        const notice = {
          id: `notice-${event.id || randomId()}`,
          playerId,
          message: validation.error || 'Nieprawidłowy wynik (tunel/tora wyjścia).',
          kind: 'warning' as const,
          ts: Date.now(),
        };
        newState = pushNotice({ ...state, events: nextEvents }, notice);
        break;
      }
      const { player: updatedPlayer, isWinner: reachedWin, hasFirstEntry } = applyScoreToPlayer(
        prevPlayer,
        delta,
        resolvedResulting,
      );
      const nextFirstEntries = hasFirstEntry
        ? { ...(state.firstEntries || {}), [playerId]: true }
        : state.firstEntries;
      const playersCopy = [...state.players];
      playersCopy[playerIndex] = updatedPlayer;
      const positioned = updatePlayerPositions(playersCopy);
      const finalIndex = positioned.findIndex(p => p.id === playerId);
      let nextIndex = state.currentPlayerIndex;
      let nextTurnNumber = state.turnNumber;
      if (state.mode === 'ONLINE') {
        // ONLINE: tylko TURN_END przesuwa turę. SCORE/PENALTY są wyłącznie rozliczeniem punktów.
        // Dzięki temu kolejność SCORE<->TURN_END nie powoduje podwójnego advance.
        if (reachedWin && finalIndex >= 0) {
          nextIndex = finalIndex;
        }
      } else {
        const progression = reachedWin
          ? { nextPlayerId: positioned[finalIndex]?.id || null, nextTurnNumber: state.turnNumber }
          : computeTurnAdvance({ ...state, players: positioned }, positioned, playerId);
        nextIndex = progression.nextPlayerId
          ? positioned.findIndex(p => p.id === progression.nextPlayerId)
          : finalIndex;
        nextTurnNumber = progression.nextTurnNumber;
      }
      newState = {
        ...state,
        players: positioned,
        currentPlayerIndex: nextIndex,
        turnNumber: nextTurnNumber,
        firstEntries: nextFirstEntries,
        isGameOver: state.isGameOver || reachedWin,
        winner: reachedWin ? positioned[finalIndex] : state.winner,
        events: nextEvents,
      };
      break;
    }
    case 'PENALTY': {
      const payload = event.payload;
      const playerId = payload.playerId;
      const deltaRaw = Number(payload.delta ?? 0);
      const delta = Number.isFinite(deltaRaw) ? deltaRaw : 0;
      const reason = payload.reason;
      const playerIndex = state.players.findIndex(p => p.id === playerId);
      if (playerIndex === -1) { newState = { ...state, events: [...(state.events || []), event] }; break; }
      const isRapidTimePenalty = reason === 'RAPID_TIME';
      const hasSamePenaltyForTurn = hasPenaltySettlementForTurn(state, event.turn, playerId, reason);
      const hasBlockingSettlementForTurn = !isRapidTimePenalty && (
        hasScoreSettlementForTurn(state, event.turn, playerId)
        || hasPenaltySettlementForTurn(state, event.turn, playerId)
      );
      if (hasSamePenaltyForTurn || hasBlockingSettlementForTurn) {
        newState = { ...state, events: [...(state.events || []), event] };
        break;
      }
      const prevPlayer = state.players[playerIndex];
      const prevScore = prevPlayer.score;
      const rawResulting = payload.resultingScore;
      const parsedResulting = rawResulting == null ? undefined : Number(rawResulting);
      const resolvedResulting = Number.isFinite(parsedResulting) ? (parsedResulting as number) : prevScore + delta;
      const normalizedEvent = {
        ...event,
        payload: { ...payload, resultingScore: resolvedResulting },
      };
      const nextEvents = [...(state.events || []), normalizedEvent];
      const isProtected = prevPlayer.score >= gameRules.scoring.minScoringDice.thresholdScore || resolvedResulting >= gameRules.scoring.minScoringDice.thresholdScore;
      if (isProtected) {
        if (isVerboseLoggingEnabled()) {
          logger.warn?.('PENALTY_AFTER_900_IGNORED', { playerId, reason, prevScore: prevPlayer.score, delta });
        }
        const notice = {
          id: `notice-${event.id || randomId()}`,
          playerId,
          message: 'Po 900 punktach kara za furę w pierwszym rzucie nie jest naliczana.',
          kind: 'warning' as const,
          ts: Date.now(),
        };
        newState = pushNotice({ ...state, events: nextEvents }, notice);
        break;
      }
      let updatedPlayer: Player = {
        ...prevPlayer,
        score: resolvedResulting,
      };
      updatedPlayer = applyTurnStats(updatedPlayer, {
        previousScore: prevScore,
        newScore: resolvedResulting,
        baseDelta: delta,
        isPass: resolvedResulting <= prevScore,
      });
      const playersCopy = [...state.players];
      playersCopy[playerIndex] = updatedPlayer;
      const positioned = updatePlayerPositions(playersCopy);
      let nextIndex = state.currentPlayerIndex;
      let nextTurnNumber = state.turnNumber;
      if (state.mode !== 'ONLINE') {
        const advance = computeTurnAdvance({ ...state, players: positioned }, positioned, playerId);
        nextIndex = advance.nextPlayerId
          ? positioned.findIndex(p => p.id === advance.nextPlayerId)
          : state.currentPlayerIndex;
        nextTurnNumber = advance.nextTurnNumber;
      }
      newState = {
        ...state,
        players: positioned,
        currentPlayerIndex: nextIndex,
        turnNumber: nextTurnNumber,
        events: nextEvents,
      };
      break;
    }
    case 'OVERTAKE': {
      const payload = event.payload;
      const overtakerId = payload.overtakerId;
      const bonus = Number(payload.bonus || 0);
      const penaltiesRaw = Array.isArray(payload.penalties) ? payload.penalties : [];
      const penalties = penaltiesRaw
        .map((p) => ({ playerId: p?.playerId, delta: Number(p?.delta ?? 0) }))
        .filter((p) => p.playerId && Number.isFinite(p.delta));
      const hasValidPayload = Boolean(overtakerId) && penalties.length === penaltiesRaw.length;
      if (!hasValidPayload) {
        if (isVerboseLoggingEnabled()) {
          logger.warn?.('OVERTAKE_PAYLOAD_INVALID', { id: event.id, turn: event.turn, overtakerId, penaltiesRaw });
        }
        newState = { ...state, events: [...(state.events || []), event] };
        break;
      }
      // W trybie REAL lokalnie naliczamy OVERTAKE w ADD_SCORE, ale zdarzenie z serwera
      // musi zostać zastosowane u widzów. Duplikaty filtrują guardy poniżej.
      const protectedVictims = penalties.filter((p: { playerId: string; delta: number }) => {
        const victim = state.players.find(pl => pl.id === p.playerId);
        return victim && victim.score >= gameRules.overtake.protectedScore;
      });
      const effectivePenalties = penalties.filter((p: { playerId: string; delta: number }) => {
        const victim = state.players.find(pl => pl.id === p.playerId);
        return victim && victim.score < gameRules.overtake.protectedScore;
      });
      const protectedCount = penalties.length - effectivePenalties.length;
      const effectiveBonus = Math.max(0, bonus - (protectedCount * gameRules.bonuses.overtake));
      // Guard against duplicate application of the same event id (can happen if replayed)
      if ((state.events || []).some((ev) => ev.id === event.id)) {
        logger.warn?.('OVERTAKE_DUP_EVENT_ID', { id: event.id, turn: event.turn, overtakerId, source: 'reducer' });
        newState = state;
        break;
      }
      // Additional guard: skip if an overtake for the same overtaker was already applied this turn
      if ((state.events || []).some((ev) => ev.type === 'OVERTAKE' && ev.turn === event.turn && ev.payload.overtakerId === overtakerId)) {
        logger.warn?.('OVERTAKE_DUP_OVERTAKER', { id: event.id, turn: event.turn, overtakerId, source: 'reducer' });
        newState = state; // całkowicie ignoruj duplikat
        break;
      }
      const nextEvents = [...(state.events || []), event];
      const playersCopy = [...state.players];
      const overtakerIndex = playersCopy.findIndex(p => p.id === overtakerId);
      let winnerCandidate: Player | undefined;
      if (overtakerIndex !== -1) {
        const prev = playersCopy[overtakerIndex];
        const stats = { ...prev.statistics };
        stats.totalOvertakes = (stats.totalOvertakes || 0) + effectivePenalties.length;
        stats.totalPointsDelta = (stats.totalPointsDelta || 0) + effectiveBonus;
        stats.averageScore = stats.totalTurns > 0 ? stats.totalPointsDelta / stats.totalTurns : 0;
        const updatedScore = prev.score + effectiveBonus;
        const updated = {
          ...prev,
          score: updatedScore,
          statistics: stats,
          isWinner: prev.isWinner || updatedScore >= gameRules.scoring.winScore,
        };
        if (updated.isWinner) {
          winnerCandidate = updated;
        }
        playersCopy[overtakerIndex] = updated;
      }
      if (protectedVictims.length > 0 && isVerboseLoggingEnabled()) {
        logger.warn?.('OVERTAKE_PROTECTED_VICTIM_IGNORED', {
          id: event.id,
          turn: event.turn,
          overtakerId,
          protectedVictims: protectedVictims.map((p: { playerId: string }) => p.playerId),
          effectiveBonus,
        });
      }
      effectivePenalties.forEach(({ playerId, delta }) => {
        const idx = playersCopy.findIndex(p => p.id === playerId);
        if (idx !== -1) {
          const victim = playersCopy[idx];
          playersCopy[idx] = { ...victim, score: victim.score + (delta || 0) };
        }
      });
      const positioned = updatePlayerPositions(playersCopy);
      const currentActiveId = state.players[state.currentPlayerIndex]?.id;
      let nextCurrentIndex = state.currentPlayerIndex;
      if (winnerCandidate) {
        const winnerIndex = positioned.findIndex(p => p.id === winnerCandidate!.id);
        if (winnerIndex !== -1) nextCurrentIndex = winnerIndex;
      } else if (currentActiveId) {
        const activeIndex = positioned.findIndex(p => p.id === currentActiveId);
        if (activeIndex !== -1) nextCurrentIndex = activeIndex;
      }
      newState = {
        ...state,
        players: positioned,
        currentPlayerIndex: nextCurrentIndex,
        isGameOver: state.isGameOver || Boolean(winnerCandidate),
        winner: winnerCandidate ? positioned[nextCurrentIndex] : state.winner,
        events: nextEvents,
      };
      break;
    }
    case 'ZONE_TRIGGERED': {
      const payload = event.payload;
      const playerId = payload.playerId;
      const zoneId = payload.zoneId;
      const playerIndex = state.players.findIndex(p => p.id === playerId);
      if (!playerId || !zoneId || playerIndex === -1) {
        newState = { ...state, events: [...(state.events || []), event] };
        break;
      }
      // Duplikat tego samego zdarzenia (replay) albo drugiego łańcucha tego gracza w turze.
      if ((state.events || []).some((ev) => ev.id === event.id)) {
        newState = state;
        break;
      }
      if ((state.events || []).some((ev) => (
        ev.type === 'ZONE_TRIGGERED'
        && ev.turn === event.turn
        && ev.payload.playerId === playerId
      ))) {
        logger.warn?.('ZONE_DUP_TRIGGER', { id: event.id, turn: event.turn, playerId, zoneId });
        newState = state;
        break;
      }

      const zoneDelta = Number(payload.zoneDelta ?? 0);
      const overtakeBonus = Number(payload.overtakeBonus ?? 0);
      const penaltiesRaw = Array.isArray(payload.penalties) ? payload.penalties : [];
      const penalties = penaltiesRaw
        .map((p) => ({ playerId: p?.playerId, delta: Number(p?.delta ?? 0) }))
        .filter((p): p is { playerId: string; delta: number } => Boolean(p.playerId) && Number.isFinite(p.delta));
      const totalDelta = zoneDelta + overtakeBonus;

      const prevPlayer = state.players[playerIndex];
      const rawResulting = Number(payload.resultingScore);
      const resultingScore = Number.isFinite(rawResulting) ? rawResulting : prevPlayer.score + totalDelta;

      const playersCopy = [...state.players];
      const stats = { ...prevPlayer.statistics };
      stats.totalPointsDelta = (stats.totalPointsDelta || 0) + totalDelta;
      if (penalties.length > 0) {
        stats.totalOvertakes = (stats.totalOvertakes || 0) + penalties.length;
      }
      stats.averageScore = stats.totalTurns > 0 ? stats.totalPointsDelta / stats.totalTurns : 0;
      const updatedPlayer = {
        ...prevPlayer,
        score: resultingScore,
        statistics: stats,
        isWinner: prevPlayer.isWinner || resultingScore >= gameRules.scoring.winScore,
      };
      playersCopy[playerIndex] = updatedPlayer;
      penalties.forEach(({ playerId: victimId, delta }) => {
        const idx = playersCopy.findIndex(p => p.id === victimId);
        if (idx !== -1) {
          playersCopy[idx] = { ...playersCopy[idx], score: playersCopy[idx].score + (delta || 0) };
        }
      });
      const positioned = updatePlayerPositions(playersCopy);

      // Odkrycie stref: upsert obsługuje serwer/REAL (strefy znane) i klienta ONLINE
      // (który nigdy nie dostał tajnej mapy i poznaje geometrię z payloadu). Odkrywamy
      // WSZYSTKIE odwiedzone strefy, także te z unieważnionej części pętli – gracz je
      // zobaczył, więc udawanie, że ich nie ma, byłoby kłamstwem wobec tego, co widział.
      const visitedLinks = Array.isArray(payload.links) && payload.links.length > 0
        ? payload.links.map((link) => ({
          zoneId: link.zoneId,
          kind: link.kind,
          tier: link.tier,
          start: Number(link.start ?? 0),
          end: Number(link.end ?? 0),
          zoneDelta: Number(link.zoneDelta ?? 0),
        }))
        : [{
          zoneId,
          kind: payload.kind,
          tier: payload.tier,
          start: Number(payload.start ?? 0),
          end: Number(payload.end ?? 0),
          zoneDelta,
        }];
      let nextZones = state.zones;
      visitedLinks.forEach((link) => {
        if (!link.zoneId) return;
        const tierSpec = gameRules.zones.tiers.find(t => t.tier === link.tier);
        nextZones = revealZone(nextZones, {
          id: link.zoneId,
          kind: link.kind,
          tier: link.tier,
          start: link.start,
          end: link.end,
          points: tierSpec ? tierSpec.points : Math.abs(link.zoneDelta),
          revealed: true,
        });
      });

      const winnerCandidate = updatedPlayer.isWinner && !prevPlayer.isWinner ? updatedPlayer : undefined;
      const currentActiveId = state.players[state.currentPlayerIndex]?.id;
      let nextCurrentIndex = state.currentPlayerIndex;
      if (winnerCandidate) {
        const winnerIndex = positioned.findIndex(p => p.id === winnerCandidate.id);
        if (winnerIndex !== -1) nextCurrentIndex = winnerIndex;
      } else if (currentActiveId) {
        const activeIndex = positioned.findIndex(p => p.id === currentActiveId);
        if (activeIndex !== -1) nextCurrentIndex = activeIndex;
      }

      newState = {
        ...state,
        players: positioned,
        currentPlayerIndex: nextCurrentIndex,
        zones: nextZones,
        isGameOver: state.isGameOver || Boolean(winnerCandidate),
        winner: winnerCandidate ? positioned[nextCurrentIndex] : state.winner,
        events: [...(state.events || []), event],
      };
      break;
    }
    case 'PLAYER_READY': {
      if (state.mode === 'REAL' || !state.lobby) return state;
      const { playerId, ready } = event.payload;
      const currentReady = state.lobby.readyMap[playerId];
      if (currentReady === ready) { return { ...state, events: [...(state.events || []), event] }; }
      newState = { ...state, lobby: { ...state.lobby, readyMap: { ...state.lobby.readyMap, [playerId]: ready } }, events: [...(state.events || []), event] };
      break;
    }
    case 'NEXT_GAME_PARTICIPATION': {
      if (state.mode === 'REAL' || !state.lobby) return state;
      const { playerId, participating } = event.payload;
      const nextGameParticipation = normalizeNextGameParticipation(state.lobby.nextGameParticipation);
      if (participating) {
        nextGameParticipation[playerId] = true;
      } else {
        delete nextGameParticipation[playerId];
      }
      newState = {
        ...state,
        lobby: {
          ...state.lobby,
          nextGameParticipation,
        },
        events: [...(state.events || []), event],
      };
      break;
    }
    case 'SETTINGS_CHANGED': {
      const { settings } = event.payload;
      newState = {
        ...state,
        settings: {
          ...state.settings,
          ...settings,
          ...(settings.rapid !== undefined
            ? { rapid: { ...state.settings.rapid, ...settings.rapid } }
            : {}),
        },
        events: [...(state.events || []), event],
      };
      break;
    }
    case 'HOST_TAKEOVER': {
      const { newHostPlayerId } = event.payload;
      const updatedPlayers = state.players.map(p => ({ ...p, isHost: p.id === newHostPlayerId }));
      newState = { ...state, players: updatedPlayers, events: [...(state.events || []), event] };
      break;
    }
    case 'VIEWER_JOINED': {
      const { viewerId, name } = event.payload;
      const existingSpectators = state.spectators || [];
      if (existingSpectators.some(s => s.id === viewerId)) {
        newState = { ...state, events: [...(state.events || []), event] };
        break;
      }
      const nextSpectators = [...existingSpectators, { id: viewerId, name, joinedAt: event.timestamp }];
      newState = { ...state, spectators: nextSpectators, events: [...(state.events || []), event] };
      break;
    }
    case 'PLAYER_LEFT': {
      const { playerId, reason } = event.payload;
      const playerIdx = state.players.findIndex(p => p.id === playerId);
      const nextEvents = [...(state.events || []), event];
      if (playerIdx === -1) {
        newState = { ...state, events: nextEvents };
        break;
      }
      const playerToLeave = state.players[playerIdx];
      if (reason === 'HOST_REMOVED_BOT' && playerToLeave.isBot === true) {
        const filteredPlayers = state.players.filter((p) => p.id !== playerId);
        const nextReadyMap = { ...(state.lobby?.readyMap || {}) };
        delete nextReadyMap[playerId];
        const nextLobby = state.lobby
          ? {
              ...state.lobby,
              order: state.lobby.order ? state.lobby.order.filter((id) => id !== playerId) : state.lobby.order,
              readyMap: nextReadyMap,
            }
          : state.lobby;
        const nextCurrentIndex = filteredPlayers.length === 0
          ? 0
          : Math.min(state.currentPlayerIndex, filteredPlayers.length - 1);
        const nextTurnCycle = state.turnCycle?.activePlayerId === playerId
          ? undefined
          : state.turnCycle;
        newState = {
          ...state,
          players: filteredPlayers,
          events: nextEvents,
          lobby: nextLobby,
          currentPlayerIndex: nextCurrentIndex,
          turnCycle: nextTurnCycle,
        };
        break;
      }
      const updatedPlayers = state.players.map(p => (p.id === playerId ? { ...p, status: 'left' as const } : p));
      const nextLobby = state.lobby
        ? {
            ...state.lobby,
            order: state.lobby.order ? state.lobby.order.filter(id => id !== playerId) : state.lobby.order,
            readyMap: { ...(state.lobby.readyMap || {}), [playerId]: false },
          }
        : state.lobby;
      let nextCurrentIndex = state.currentPlayerIndex;
      let nextTurnCycle = state.turnCycle;
      let nextTurnNumber = state.turnNumber;
      if (state.turnCycle?.activePlayerId === playerId) {
        const advance = computeTurnAdvance({ ...state, players: updatedPlayers, lobby: nextLobby }, updatedPlayers, playerId);
        const nextId = advance.nextPlayerId;
        nextTurnNumber = advance.nextTurnNumber;
        nextCurrentIndex = nextId ? updatedPlayers.findIndex(p => p.id === nextId) : nextCurrentIndex;
        nextTurnCycle = nextId
          ? { activePlayerId: nextId, rollCount: 0, dice: [], kept: makeEmptyDiceMask(), capturedMask: makeEmptyDiceMask(), status: 'IDLE' as const, diceOrigins: [] }
          : undefined;
      }
      newState = { ...state, players: updatedPlayers, events: nextEvents, lobby: nextLobby, currentPlayerIndex: nextCurrentIndex, turnCycle: nextTurnCycle, turnNumber: nextTurnNumber };
      break;
    }
    case 'GAME_RESTARTED': {
      const classifiedRestart = classifyGameRestartedPayload(state.id, event.payload || {});
      if (classifiedRestart.kind !== 'supported-same-room') {
        newState = { ...state, events: [...(state.events || []), event] };
        break;
      }
      newState = {
        ...state,
        id: state.id,
        roomId: state.roomId || state.groupToken || state.id,
        currentRoundId: classifiedRestart.currentRoundId,
        players: [],
        spectators: [],
        currentPlayerIndex: 0,
        turnNumber: 1,
        isGameOver: false,
        winner: undefined,
        postGameSummary: null,
        history: [],
        events: [event],
        notices: [],
        firstEntries: {},
        lobby: state.mode === 'ONLINE' ? { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} } : undefined,
        turnCycle: undefined,
        rapidTurnMeta: null,
        rapidPenaltyPresentation: null,
        zones: undefined, // nowa runda = nowa, świeżo losowana mapa stref
        segments: [],
        startedAt: event.timestamp,
        segmentsOwnerId: undefined,
      };
      break;
    }
    case 'ORDER_FINALIZED': {
      if (state.mode === 'REAL' || !state.lobby) { newState = appendEventOnly(); break; }
      // Guard: jeśli gra już w IN_GAME, ignorujemy spóźnione / zdublowane ORDER_FINALIZED.
      const hasTurnStarted = !!state.turnCycle || state.lobby.phase === 'IN_GAME';
      if (hasTurnStarted) { newState = appendEventOnly(); break; }
      const { order } = event.payload;
      const playersMap = new Map(state.players.map(p => [p.id, p] as const));
      const reordered = order.map((id, idx) => {
        const p = playersMap.get(id);
        return p ? { ...p, position: idx + 1 } : undefined;
      }).filter(Boolean) as Player[];
      const remaining = state.players.filter(p => !order.includes(p.id));
      const nextPlayers = [...reordered, ...remaining];
      const firstId = order[0];
      const firstIdx = nextPlayers.findIndex(p => p.id === firstId);
      const nextTurnCycle = state.turnCycle ?? (firstId ? {
        activePlayerId: firstId,
        rollCount: 0,
        dice: [],
        kept: makeEmptyDiceMask(),
        capturedMask: makeEmptyDiceMask(),
        status: 'IDLE' as const,
        diceOrigins: [],
      } : undefined);
      newState = {
        ...state,
        players: nextPlayers,
        currentPlayerIndex: firstIdx >= 0 ? firstIdx : state.currentPlayerIndex,
        lobby: { ...state.lobby, order, phase: 'ORDER_REVEAL', readyMap: { ...state.lobby.readyMap } },
        turnCycle: nextTurnCycle,
        events: [...(state.events || []), event],
      };
      break;
    }
    case 'GAME_STARTED': {
      if (state.mode === 'REAL' || !state.lobby || state.lobby.phase !== 'ORDER_REVEAL' || !state.lobby.order?.length) { newState = appendEventOnly(); break; }
      const firstId = event.payload.firstPlayerId || state.lobby.order[0];
      const firstIdx = state.players.findIndex(p => p.id === firstId);
      const nextTurnCycle = {
        activePlayerId: firstId,
        rollCount: 0,
        dice: [],
        kept: makeEmptyDiceMask(),
        capturedMask: makeEmptyDiceMask(),
        status: 'IDLE' as const,
        diceOrigins: [],
      };
      newState = {
        ...state,
        lobby: { ...state.lobby, phase: 'IN_GAME' },
        currentPlayerIndex: firstIdx >= 0 ? firstIdx : state.currentPlayerIndex,
        turnCycle: nextTurnCycle,
        events: [...(state.events || []), event],
      };
      break;
    }
    case 'DICE_ROLL': {
      newState = handleDiceRollEvent({
        state,
        event,
        isVerboseLoggingEnabled,
        logger,
        perf,
      });
      break;
    }
    case 'DICE_KEEP': {
      newState = handleDiceKeepEvent(state, event);
      break;
    }
    case 'TURN_END': {
      newState = handleTurnEnd({ state, event, perf });
      break;
    }
    default: newState = { ...state, events: [...(state.events || []), event] }; break;
  }
  return newState;
}

export function handleTrimEvents(state: GameState, events: GameEvent[]): GameState {
  return { ...state, events };
}
